/**
 * FR3 — proof-source options for the Create-home proof picker (mock 02).
 *
 * Lists the project's reviews and bigNumbers as pickable chips whose ids land
 * in `sourceRefs.reviewIds/bigNumberIds` on generate-batch — making the
 * model's `@review[...]`/`@bigNumber[...]` citations resolvable end-to-end.
 * Reads ride the firebase-core services (the same data the legacy lab
 * source-picker surfaces); one-shot loads, no listeners.
 */

import { ReviewService } from '@modules/firebase-core/services/ReviewService';
import { BigNumberService } from '@modules/firebase-core/services/BigNumberService';

export interface AdCreativeProofOption {
  id: string;
  kind: 'review' | 'bigNumber';
  /** Short display snippet (quote or metric line). */
  snippet: string;
}

function reviewSnippet(record: Record<string, unknown>): string {
  const text = String(record.reviewtext ?? record.reviewText ?? record.text ?? '').trim();
  const author = String(record.author ?? record.authorName ?? '').trim();
  const base = text.length > 80 ? `${text.slice(0, 80)}…` : text;
  return author ? `“${base}” — ${author}` : `“${base}”`;
}

function bigNumberSnippet(record: Record<string, unknown>): string {
  const value = String(record.value ?? record.number ?? '').trim();
  const label = String(record.label ?? record.description ?? record.title ?? '').trim();
  return [value, label].filter(Boolean).join(' — ');
}

export async function loadProofSourceOptions(projectId: string): Promise<AdCreativeProofOption[]> {
  const [reviews, bigNumbers] = await Promise.all([
    new ReviewService(projectId).list({ limit: 30 }).catch(() => []),
    new BigNumberService(projectId).list({ limit: 30 }).catch(() => []),
  ]);
  const options: AdCreativeProofOption[] = [];
  for (const review of reviews) {
    const record = review as unknown as Record<string, unknown>;
    const id = String(record.id ?? '');
    if (!id) continue;
    options.push({ id, kind: 'review', snippet: reviewSnippet(record) });
  }
  for (const bigNumber of bigNumbers) {
    const record = bigNumber as unknown as Record<string, unknown>;
    const id = String(record.id ?? '');
    if (!id) continue;
    options.push({ id, kind: 'bigNumber', snippet: bigNumberSnippet(record) });
  }
  return options;
}
